/**
 * Fila de troféus pendentes de exibição.
 *
 * O detector (`useBadgeUnlockDetector`) enfileira conquistas novas; o
 * overlay (`TrophyNotification`) consome uma por vez. Context simples —
 * mostra um banner, ao fechar avança pro próximo.
 */

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import type { UserBadge } from '@/services/badges.service'
import { trophyUid } from '@/lib/trophy-storage'

export interface TrophyItem {
  /** `badgeId::cycleKey` — chave estável pro AnimatePresence. */
  uid: string
  userBadge: UserBadge
}

/** Teto da fila — protege contra grant em lote inundar a tela. */
const MAX_QUEUE = 8

interface TrophyQueueValue {
  current: TrophyItem | null
  enqueue: (badges: UserBadge[]) => void
  dismissCurrent: () => void
}

const TrophyQueueContext = createContext<TrophyQueueValue | null>(null)

export function TrophyQueueProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<TrophyItem[]>([])

  const enqueue = useCallback((badges: UserBadge[]) => {
    if (badges.length === 0) return
    setQueue((prev) => {
      const known = new Set(prev.map((item) => item.uid))
      const additions: TrophyItem[] = []
      for (const ub of badges) {
        const uid = trophyUid(ub)
        if (known.has(uid)) continue
        known.add(uid)
        additions.push({ uid, userBadge: ub })
      }
      return [...prev, ...additions].slice(0, MAX_QUEUE)
    })
  }, [])

  const dismissCurrent = useCallback(() => {
    setQueue((prev) => prev.slice(1))
  }, [])

  const value = useMemo<TrophyQueueValue>(
    () => ({ current: queue[0] ?? null, enqueue, dismissCurrent }),
    [queue, enqueue, dismissCurrent]
  )

  return <TrophyQueueContext.Provider value={value}>{children}</TrophyQueueContext.Provider>
}

export function useTrophyQueue(): TrophyQueueValue {
  const ctx = useContext(TrophyQueueContext)
  if (!ctx) {
    throw new Error('useTrophyQueue precisa estar dentro de <TrophyQueueProvider>')
  }
  return ctx
}
